import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { programs, users, photos } from '@cwos/api-client';
import type { users as UsersNS } from '@cwos/api-client';
import { Avatar, ConfirmAction, Pill, SkeletonSet } from '@cwos/ui-admin';
import type { Severity } from '@cwos/ui-shared';
import { useAuth } from '../../lib/auth.js';
import { usePermissions } from '../permissions.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsHeader } from './common.js';
import { rosterCell } from './organizationLogic.js';
import { LoadFailure } from '../LoadFailure.js';

const STATUS_TONE: Record<UsersNS.AccountStatus, { variant: Severity; label: string }> = {
  active: { variant: 'good', label: 'Active' },
  invited: { variant: 'sky', label: 'Invited' },
  suspended: { variant: 'urgent', label: 'Suspended' },
  deactivated: { variant: 'plain', label: 'Deactivated' },
};

function statusTone(status: UsersNS.AccountStatus): { variant: Severity; label: string } {
  return STATUS_TONE[status] ?? { variant: 'plain', label: status };
}

/**
 * Person detail (doc 13 T9/T11). Profile + role/permission grants + account
 * lifecycle. Role/permission assignment is gated `role:assign`; suspend/
 * deactivate/reactivate are gated `user:manage` and never target the acting
 * principal (the backend refuses self account changes).
 */
export function PersonDetail() {
  const params = useParams({ strict: false }) as { userId?: string };
  const id = params.userId ?? '';
  const navigate = useNav();
  const qc = useQueryClient();
  const { has } = usePermissions();
  const { me, retry: refreshMe } = useAuth();
  const canManage = has('user:manage');
  const canAssign = has('role:assign');
  // The photo write gate mirrors the server exactly: one's OWN photo is
  // self-service; anyone else's needs user:manage. The server refuses
  // regardless of what is drawn here.
  const isSelf = me?.userId === id;
  const canEditPhoto = canManage || isSelf;

  const person = useQuery({
    queryKey: ['users', 'person', id],
    queryFn: () => users.person(id),
    enabled: id.length > 0,
  });

  const suspend = useMutation({
    mutationFn: () => users.suspend(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const deactivate = useMutation({
    mutationFn: () => users.deactivate(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
  const reactivate = useMutation({
    mutationFn: () => users.reactivate(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });
  /**
   * A failed lifecycle action used to fail into silence: no onError, nothing
   * rendered, and the administrator who pressed Suspend on somebody walks away
   * believing their sessions ended. The status pill still says the truth, but
   * nothing pointed at it. One line, from whichever action last failed —
   * they are mutually exclusive buttons on one card.
   */
  const accountActionError = suspend.error ?? deactivate.error ?? reactivate.error ?? null;

  const photoInput = useRef<HTMLInputElement | null>(null);
  const uploadPhoto = useMutation({
    mutationFn: (file: File) => users.uploadPhoto(id, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['directory', 'workers'] });
      // Own photo also lives in the shell's account menu, fed by /auth/me.
      if (isSelf) refreshMe();
    },
  });
  const removePhoto = useMutation({
    mutationFn: () => users.removePhoto(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['directory', 'workers'] });
      if (isSelf) refreshMe();
    },
  });
  const [confirmRemovePhoto, setConfirmRemovePhoto] = useState(false);
  const photoBusy = uploadPhoto.isPending || removePhoto.isPending;
  const photoError = uploadPhoto.error ?? removePhoto.error;

  const confirmThen = (message: string, action: () => void) => {
    if (window.confirm(message)) action();
  };

  const p = person.data;

  return (
    <div className="rn-col" style={{ maxWidth: 720 }}>
      <SettingsHeader title={p ? p.name || p.email : 'Person'} />

      {person.isLoading ? (
        <SkeletonSet surface="table" rows={5} />
      ) : person.isError && !p ? (
        <LoadFailure
          error={person.error}
          mainRead
          message="We couldn’t load this person just now."
          onRetry={() => void person.refetch()}
        />
      ) : p ? (
        <>
          <SettingsCard>
            <div className="rn-card-head">
              <h2 className="rn-card-title">Profile</h2>
              <Pill variant={statusTone(p.status).variant} dot>
                {statusTone(p.status).label}
              </Pill>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-12)',
                marginBottom: 'var(--space-12)',
              }}
            >
              <Avatar
                name={p.name || p.email}
                size={56}
                src={photos.personPhotoSrc({ userId: p.id, photoUpdatedAt: p.photoUpdatedAt })}
              />
              {canEditPhoto ? (
                <div className="rn-col" style={{ gap: 'var(--space-4)' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
                    <button
                      type="button"
                      className="signin-devbtn"
                      style={{ width: 'auto', padding: 'var(--space-6) var(--space-12)' }}
                      disabled={photoBusy}
                      onClick={() => photoInput.current?.click()}
                    >
                      {p.photoUpdatedAt ? 'Replace photo' : 'Add photo'}
                    </button>
                    {p.photoUpdatedAt ? (
                      <button
                        type="button"
                        className="signin-devbtn"
                        style={{ width: 'auto', padding: 'var(--space-6) var(--space-12)' }}
                        disabled={photoBusy}
                        onClick={() => setConfirmRemovePhoto(true)}
                      >
                        Remove photo
                      </button>
                    ) : null}
                    {/* PH-UX-004. The mildest of the twelve, and the one this
                        file's three OTHER confirm mechanisms all skipped:
                        `confirmThen` wraps Reactivate/Suspend/Deactivate and
                        the two `window.confirm`s wrap role and permission
                        removal, so a `grep -c confirm` on this file was
                        reassuring and wrong about this control. */}
                    <ConfirmAction
                      open={confirmRemovePhoto}
                      title={`Remove ${p.name || p.email}’s photo?`}
                      consequence={
                        `This deletes the stored photo for ${p.name || p.email}. We do not keep ` +
                        `a copy, so putting it back means uploading the original file again — ` +
                        `if you no longer have that file, it is gone. Their account, roles and ` +
                        `permissions are not affected.`
                      }
                      confirmLabel="Remove photo"
                      pendingLabel="Removing…"
                      pending={removePhoto.isPending}
                      onCancel={() => setConfirmRemovePhoto(false)}
                      onConfirm={() => removePhoto.mutate()}
                    />
                    <input
                      ref={photoInput}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) uploadPhoto.mutate(file);
                      }}
                    />
                  </div>
                  <span className="rn-muted" style={{ fontSize: 'var(--text-xs, 12px)' }}>
                    JPEG, PNG, or WebP, up to 5 MB.
                  </span>
                  {photoError ? (
                    <span
                      style={{ color: 'var(--color-urgent)', fontSize: 'var(--text-xs, 12px)' }}
                    >
                      {photoError instanceof Error
                        ? photoError.message
                        : 'The photo change failed.'}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                gap: 'var(--space-8) var(--space-16)',
                margin: 0,
              }}
            >
              <dt className="rn-muted">Email</dt>
              <dd style={{ margin: 0 }}>{p.email}</dd>
              <dt className="rn-muted">Role title</dt>
              <dd style={{ margin: 0 }}>{p.roleTitle ?? '—'}</dd>
              <dt className="rn-muted">Expected weekly hours</dt>
              <dd style={{ margin: 0 }}>
                {p.expectedWeeklyHours != null ? String(p.expectedWeeklyHours) : '—'}
              </dd>
              <dt className="rn-muted">Work assignment</dt>
              <dd style={{ margin: 0 }}>
                {p.primaryProgramName
                  ? p.primaryProgramLocationName
                    ? `${p.primaryProgramName} · ${p.primaryProgramLocationName}`
                    : p.primaryProgramName
                  : '—'}
              </dd>
              <dt className="rn-muted">Last active</dt>
              <dd style={{ margin: 0 }}>
                {p.lastLoginAt ? new Date(p.lastLoginAt).toLocaleString() : '—'}
              </dd>
              <dt className="rn-muted">Roles</dt>
              <dd style={{ margin: 0 }}>
                {p.roles.length ? p.roles.map((r) => r.name).join(' · ') : '—'}
              </dd>
              {p.pendingInvitations.length > 0 ? (
                <>
                  <dt className="rn-muted">Invitation</dt>
                  <dd style={{ margin: 0 }}>
                    Invite pending for {p.pendingInvitations[0]!.email} as{' '}
                    {p.pendingInvitations[0]!.roleName}
                  </dd>
                </>
              ) : null}
            </dl>
          </SettingsCard>

          <OrganizationContext workerId={p.workerId} />

          {canManage ? (
            <SettingsCard>
              <h2 className="rn-card-title">Account</h2>
              <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-12)' }}>
                {p.status === 'suspended' ? (
                  <button
                    type="button"
                    className="signin-devbtn"
                    style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                    disabled={reactivate.isPending}
                    onClick={() =>
                      confirmThen(
                        'Reactivate this account? They’ll be able to sign in again.',
                        () => reactivate.mutate(),
                      )
                    }
                  >
                    Reactivate
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="signin-devbtn"
                      style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                      disabled={suspend.isPending}
                      onClick={() =>
                        confirmThen('Suspend this account? Their sessions end immediately.', () =>
                          suspend.mutate(),
                        )
                      }
                    >
                      Suspend
                    </button>
                    <button
                      type="button"
                      className="signin-devbtn"
                      style={{
                        width: 'auto',
                        padding: 'var(--space-10) var(--space-16)',
                        background: 'var(--color-urgent)',
                      }}
                      disabled={deactivate.isPending}
                      onClick={() =>
                        confirmThen(
                          'Deactivate this account? Their sessions end immediately and they can’t sign in again.',
                          () => deactivate.mutate(),
                        )
                      }
                    >
                      Deactivate
                    </button>
                  </>
                )}
              </div>
              {accountActionError ? (
                <p
                  className="rn-muted"
                  role="alert"
                  style={{ color: 'var(--color-urgent-deep)', marginTop: 'var(--space-8)' }}
                >
                  That didn’t save — this account is unchanged.{' '}
                  {accountActionError instanceof Error ? accountActionError.message : ''}
                </p>
              ) : null}
            </SettingsCard>
          ) : null}

          {canAssign ? <GrantsCard userId={id} /> : null}

          <div>
            <button type="button" className="rn-link" onClick={() => navigate('/settings/people')}>
              ← Back to people
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function GrantsCard({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const grants = useQuery({
    queryKey: ['users', 'grants', userId],
    queryFn: () => users.grants(userId),
  });

  const [roleKey, setRoleKey] = useState('');
  const [permKey, setPermKey] = useState('');

  const assignRole = useMutation({
    mutationFn: () => users.assignRole(userId, { roleKey }),
    onSuccess: () => {
      setRoleKey('');
      void qc.invalidateQueries({ queryKey: ['users', 'grants', userId] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
  const removeRole = useMutation({
    mutationFn: (grantId: string) => users.removeRole(userId, grantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users', 'grants', userId] }),
  });
  const grantPerm = useMutation({
    mutationFn: () => users.grantPermission(userId, { permissionKey: permKey }),
    onSuccess: () => {
      setPermKey('');
      void qc.invalidateQueries({ queryKey: ['users', 'grants', userId] });
    },
  });
  const revokePerm = useMutation({
    mutationFn: (grantId: string) => users.revokePermission(userId, grantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users', 'grants', userId] }),
  });
  /** Whichever grant change last failed — see the alert line below. */
  const grantsActionError =
    assignRole.error ?? removeRole.error ?? grantPerm.error ?? revokePerm.error ?? null;

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Roles & permissions</h2>
      {grants.isLoading ? (
        <SkeletonSet surface="table" rows={4} />
      ) : grants.isError && !grants.data ? (
        <LoadFailure
          error={grants.error}
          message="We couldn’t load grants just now."
          onRetry={() => void grants.refetch()}
        />
      ) : grants.data ? (
        <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-16)' }}>
          <div>
            <span className="rn-muted">Roles</span>
            <ul
              style={{
                listStyle: 'none',
                margin: 'var(--space-8) 0 0',
                padding: 0,
                display: 'grid',
                gap: 'var(--space-8)',
              }}
            >
              {grants.data.roles.map((r: UsersNS.RoleGrant) => (
                <li
                  key={r.grantId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid var(--color-line)',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--space-8) var(--space-12)',
                  }}
                >
                  <span>
                    {r.name}
                    {r.scopeType !== 'org' ? (
                      <span className="rn-muted"> · {r.scopeType} scoped</span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="rn-link"
                    // The visible word is enough for a sighted reader because
                    // the role name sits beside it; a screen-reader user
                    // tabbing a list of grants hears "Remove" N times with
                    // nothing to tell them which press removes which.
                    aria-label={`Remove the ${r.name} role`}
                    disabled={removeRole.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove the ${r.name} role from this person? Everything that role granted stops working immediately.`,
                        )
                      ) {
                        removeRole.mutate(r.grantId);
                      }
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-10)' }}>
              <select
                style={{ marginBottom: 0 }}
                // Named for assistive technology: the placeholder option is
                // only a name while nothing is chosen, which is exactly the
                // moment it stops being one.
                aria-label="Role to add"
                value={roleKey}
                onChange={(e) => setRoleKey(e.target.value)}
              >
                <option value="" disabled>
                  Add a role…
                </option>
                {grants.data.availableRoles
                  .filter((r) => !grants.data!.roles.some((g) => g.key === r.key))
                  .map((r) => (
                    <option key={r.id} value={r.key}>
                      {r.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-8) var(--space-14)' }}
                disabled={assignRole.isPending || !roleKey}
                onClick={() => assignRole.mutate()}
              >
                Assign
              </button>
            </div>
          </div>

          <div>
            <span className="rn-muted">Direct permissions</span>
            <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              Granted to this person only — never implied by their role. Every use is recorded.
            </p>
            <ul
              style={{
                listStyle: 'none',
                margin: 'var(--space-8) 0 0',
                padding: 0,
                display: 'grid',
                gap: 'var(--space-8)',
              }}
            >
              {grants.data.directPermissions.map((perm) => (
                <li
                  key={perm.grantId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid var(--color-line)',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--space-8) var(--space-12)',
                  }}
                >
                  <span>
                    <span style={{ fontWeight: 600 }}>{perm.key}</span>{' '}
                    <span className="rn-muted">{perm.description}</span>
                  </span>
                  <button
                    type="button"
                    className="rn-link"
                    aria-label={`Revoke the ${perm.key} permission`}
                    disabled={revokePerm.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Revoke ${perm.key} from this person? Anything it alone allowed stops working immediately.`,
                        )
                      ) {
                        revokePerm.mutate(perm.grantId);
                      }
                    }}
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-10)' }}>
              <select
                style={{ marginBottom: 0 }}
                aria-label="Permission to grant"
                value={permKey}
                onChange={(e) => setPermKey(e.target.value)}
              >
                <option value="" disabled>
                  Grant a permission…
                </option>
                {grants.data.availablePermissions
                  .filter((p) => !grants.data!.directPermissions.some((g) => g.key === p.key))
                  .map((p) => (
                    <option key={p.id} value={p.key}>
                      {p.key}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-8) var(--space-14)' }}
                disabled={grantPerm.isPending || !permKey}
                onClick={() => grantPerm.mutate()}
              >
                Grant
              </button>
            </div>
          </div>

          {/* The four grant mutations had no error branch anywhere: a failed
              removal left the role on screen and said nothing, which reads as
              "done" to the person who clicked. One line, naming the last
              failure — access changes are the one place a silent failure is
              indistinguishable from a security decision. */}
          {grantsActionError ? (
            <p
              className="rn-muted"
              role="alert"
              style={{ color: 'var(--color-urgent-deep)', margin: 0 }}
            >
              That change didn’t save — the grants above are unchanged.{' '}
              {grantsActionError instanceof Error ? grantsActionError.message : ''}
            </p>
          ) : null}
        </div>
      ) : null}
    </SettingsCard>
  );
}

/**
 * Where this person sits in the organization: the programs they belong to and
 * which one is primary.
 *
 * Only relationships that actually exist are shown. A login with no workforce
 * record has no program context at all, and says so rather than rendering an
 * empty scaffold — and the whole card is absent for a principal without
 * `program:read`, because a hidden control is not a security boundary but a
 * visible one they cannot use is just noise.
 */
function OrganizationContext({ workerId }: { workerId: string | null }) {
  const { has } = usePermissions();
  const navigate = useNav();
  const canRead = has('program:read');

  const memberships = useQuery({
    queryKey: ['programs', 'worker', workerId],
    queryFn: () => programs.forWorker(workerId!),
    enabled: canRead && workerId != null,
  });

  if (!canRead) return null;

  if (workerId == null) {
    return (
      <SettingsCard>
        <h2 className="rn-card-title">Organization</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          This person has a login but no workforce record, so they aren’t assigned to any program.
        </p>
      </SettingsCard>
    );
  }

  const rows = memberships.data ?? [];
  const primary = rows.find((r) => r.isPrimary);
  const others = rows.filter((r) => !r.isPrimary);

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Organization</h2>
      {/* "Not assigned to a program yet" was rendered from a failed read, on a
          card an administrator uses to decide whether somebody's access is set
          up correctly. */}
      {memberships.isLoading ? (
        <SkeletonSet surface="table" rows={2} />
      ) : memberships.isError && memberships.data === undefined ? (
        <LoadFailure
          error={memberships.error}
          message="We couldn’t read this person’s program membership just now."
          onRetry={() => void memberships.refetch()}
        />
      ) : rows.length === 0 ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Not assigned to a program yet. Some organizations run entirely without programs — this is
          only a gap if yours does not.
        </p>
      ) : (
        <dl className="org-facts">
          <div>
            <dt>Primary program</dt>
            <dd>
              {primary ? (
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => navigate(`/settings/programs/${primary.programId}`)}
                >
                  {primary.programName}
                </button>
              ) : (
                'Not set'
              )}
            </dd>
          </div>
          <div>
            <dt>Also works in</dt>
            <dd>{others.length > 0 ? others.map((o) => o.programName).join(', ') : '—'}</dd>
          </div>
          <div>
            <dt>Reports to</dt>
            {/* `?? '—'` on this row said THIS PERSON HAS NO SUPERVISOR. For a
                caller with `program:read` and not `user:read` the name is null
                because `identityWithheld` nulled it, and an administrator
                reading this card to decide whether somebody's reporting line is
                set up correctly acted on the em dash. */}
            <dd>{rows[0] ? rosterCell(rows[0], rows[0].supervisorName, '—') : '—'}</dd>
          </div>
        </dl>
      )}
    </SettingsCard>
  );
}
